import React, { useContext } from "react";
import { useEffect, useRef, useState } from "react";
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { cityWithPreposition } from "../../../../components/types/types";
import { AppContext } from "../../../../context/AppContext";
import './homeView.css'
import { ContainerRestauLight } from "../../../../components/containerRestau/containerRestauLight";
import { HomeSection } from "../../HomeScreen";
import HookSortedPart from "../../../../components/hookHomeTopBar/hookSortedPart";
import { RestaurantContext } from "../../../../context/RestaurantContext";

const SKELETON_CARDS = 5;
const SKELETON_SECTIONS = 3;
 
const SkeletonCard = () => (
  <div className="homeSkeletonCard">
    <div className="homeSkeletonImage" />

    <div className="homeSkeletonGoldLine" />

    <div className="homeSkeletonContent">
      <div className="homeSkeletonFirstLine">
        <div className="homeSkeletonLine homeSkeletonTitleLine" />
        <div className="homeSkeletonLine homeSkeletonRateLine" />
      </div>

      <div className="homeSkeletonSecondLine">
        <div className="homeSkeletonLine homeSkeletonTypeLine" />
        <div className="homeSkeletonLine homeSkeletonPriceLine" />
      </div>

      <div className="homeSkeletonLine homeSkeletonThirdLine" />
      <div className="homeSkeletonLine homeSkeletonFourthLine" />
    </div>
  </div>
);
 
const SkeletonSection = () => (
  <div className="homeSkeletonSection">
    <div className="homeSkeletonTitle" />
    <div className="homeSkeletonList">
      {Array.from({ length: SKELETON_CARDS }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  </div>
);
 
const HomeSkeleton = () => (
  <div className="homeView">
    {Array.from({ length: SKELETON_SECTIONS }).map((_, i) => (
      <SkeletonSection key={i} />
    ))}
  </div>
);


interface ScrollState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

interface HomeViewProps {
  sections: HomeSection[];
  loading: boolean;
  refreshLoading: boolean;

  fetchSections: (url: string,elem:string, options?: RequestInit, isInitialLoad?: boolean) => void;
  baseSortSection:string,
  setShowWaitingSearch: React.Dispatch<React.SetStateAction<boolean>>;
}

const HomeView:React.FC<HomeViewProps> = ({sections,loading,refreshLoading,fetchSections,baseSortSection,setShowWaitingSearch}) => {
  const {setFilterChoose,setDate_selected,setHour_selected,setCurrentNeighborhood} = useContext(AppContext)
  const {setFetchResto} = useContext(RestaurantContext)
  const navigate = useNavigate()
  const listRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [scrollStates, setScrollStates] = useState<Record<number, ScrollState>>({});
  const rafRef = useRef<Record<number, number | null>>({});

  useEffect(() => {
    setCurrentNeighborhood([])
  },[])
  const disableHighlight = window.innerWidth > 650 && window.matchMedia("(pointer: coarse)").matches;
  const [mobileActiveIndexes, setMobileActiveIndexes] =
    useState<Record<number, number>>(() => {
        const initial: Record<number, number> = {};

        sections.forEach(section => {
            initial[section.id] = 0;
        });

        return initial;
    });

  const updateScrollState = (sectionId: number) => {
    if (rafRef.current[sectionId]) return;

    rafRef.current[sectionId] = requestAnimationFrame(() => {
      rafRef.current[sectionId] = null;
      const elem = listRefs.current[sectionId];
      if (!elem) return;
      const maxScrollLeft = elem.scrollWidth - elem.clientWidth;
      const EPSILON = 2;
      const canScrollLeft = elem.scrollLeft > EPSILON;
      const canScrollRight = elem.scrollLeft < maxScrollLeft - EPSILON;

      setScrollStates((prev) => {
        const previous = prev[sectionId];

        if (previous?.canScrollLeft === canScrollLeft &&previous?.canScrollRight === canScrollRight) return prev;


        return {...prev,[sectionId]: {canScrollLeft,canScrollRight}};
      });
    });
  };

  const scrollSection = (sectionId: number, direction: "left" | "right") => {
    const elem = listRefs.current[sectionId];
    if (!elem) return;
    const STEP_RATIO = 0.8;
    const EDGE_RATIO = 0.2
    const step = elem.clientWidth * STEP_RATIO;
    const edgeThreshold = elem.clientWidth * EDGE_RATIO;
    const maxScrollLeft = elem.scrollWidth - elem.clientWidth;
    const current = elem.scrollLeft;
    let target: number;
    if (direction === "right") {
      const remaining = maxScrollLeft - current;
      if (remaining <= step + edgeThreshold) target = maxScrollLeft;
      else target = current + step;
    } else {
      const remaining = current;
      if (remaining <= step + edgeThreshold) target = 0;
      else target = current - step;
    }
    elem.scrollTo({left: target,behavior: "smooth"})
  };

useEffect(() => {
  setMobileActiveIndexes((prev) => {
    const next = { ...prev };

    sections.forEach((section) => {
      if (next[section.id] === undefined) {
        next[section.id] = 0;
      }
    });

    return next;
  });

  const raf = requestAnimationFrame(() => {
    sections.forEach((section) => {
      updateScrollState(section.id);
    });
  });

  const handleResize = () => {
    sections.forEach((section) => {
      updateScrollState(section.id);
    });
  };

  window.addEventListener("resize", handleResize);

  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener(
      "resize",
      handleResize
    );
  };
}, [sections]);

  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  useEffect(() => {
      const updateScrollbarWidth = () => {
          setScrollbarWidth(
              window.innerWidth - document.documentElement.clientWidth
          );
      };
      updateScrollbarWidth();
      window.addEventListener("resize", updateScrollbarWidth);
      return () => {
          window.removeEventListener("resize", updateScrollbarWidth);
      };
  }, []);
  useEffect(() => {
  if (typeof window === "undefined") return;

  const observers: IntersectionObserver[] = [];

  sections.forEach((section) => {
    const list = listRefs.current[section.id];
    if (!list) return;

    const cards = Array.from(
      list.querySelectorAll<HTMLElement>(".containerRestauLight")
    );

    if (!cards.length) return;
    const ratios = new Map<Element, number>();

    cards.forEach((card) => {
      ratios.set(card, 0);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          ratios.set(entry.target, entry.intersectionRatio);
        });

        let bestIndex = 0;
        let bestRatio = -1;

        cards.forEach((card, index) => {
          const ratio = ratios.get(card) ?? 0;

          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestIndex = index;
          }
        });

        setMobileActiveIndexes((prev) => {
          if (prev[section.id] === bestIndex) {
            return prev;
          }

          return {
            ...prev,
            [section.id]: bestIndex,
          };
        });
      },
      { root: list,threshold: [0.25, 0.4, 0.55, 0.7, 0.85],}
    );

    cards.forEach((card) => observer.observe(card));
    observers.push(observer);
  });
  return () => {
    observers.forEach((observer) => observer.disconnect());
  };
}, [sections]);

  const handleGoToBlock = (sectionId: number,indexResto: number) => {
  const list = listRefs.current[sectionId];
  if (!list) return;
  const cards = Array.from(
    list.querySelectorAll<HTMLElement>(
      ".containerRestauLight"
    )
  );
  const target = cards[indexResto];
  if (!target) return;
  target.scrollIntoView({behavior: "smooth",block: "nearest",inline: "center",});
};
  const orderedSections = React.useMemo(() => {
    return [...sections].sort((a, b) => {
      const aIsAllRestaurants = a.pageKind === "special" && a.specialKind === "all_restaurants";
      const bIsAllRestaurants = b.pageKind === "special" && b.specialKind === "all_restaurants";
      if (aIsAllRestaurants) return 1;
      if (bIsAllRestaurants) return -1;
      return 0;
    });
  }, [sections]);
  const sectionPaginationEnabled = baseSortSection !== "" && baseSortSection !== "Suggestions";
  const sectionSteps = React.useMemo(() => {
    const total = orderedSections.length;
    if (!sectionPaginationEnabled || total <= 12) return [total];
    const steps = [10];
    let current = 10;
    while (current < total) {
      const remaining = total - current;
      if (remaining <= 2) {
        steps[steps.length - 1] = total;
          break;
      }
      let next = Math.min(current + 10, total);
      if (total - next < 3) {
        next = total;
      }
      if (next === current) break;
      steps.push(next);
      current = next;
    }
    return steps;
  }, [orderedSections.length, sectionPaginationEnabled]);
  const [sectionStepIndex, setSectionStepIndex] = useState(0);
  useEffect(() => {
    setSectionStepIndex(0);
  }, [baseSortSection]);
  const safeSectionStepIndex = Math.min(sectionStepIndex,sectionSteps.length - 1);
  const visibleSectionCount = sectionPaginationEnabled ? sectionSteps[safeSectionStepIndex] : orderedSections.length;
  const visibleSections = orderedSections.slice(0, visibleSectionCount);
  const canShowMoreSections = sectionPaginationEnabled && safeSectionStepIndex < sectionSteps.length - 1;
  const handleShowMoreSections = () => {
    setSectionStepIndex((prev) =>Math.min(prev + 1, sectionSteps.length - 1));
  };

  useEffect(() => {
    return () => {
      Object.values(rafRef.current).forEach((id) => {
        if (id) cancelAnimationFrame(id);
      });
    };
  }, []);
  const isDesktop = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  useEffect(() => {
    if (refreshLoading) return;

    requestAnimationFrame(() => {
      sections.forEach((section) => {
        const list = listRefs.current[section.id];

        if (list) {
          const previousBehavior = list.style.scrollBehavior;
          list.style.scrollBehavior = "auto";
          list.scrollLeft = 0;
          requestAnimationFrame(() => {
              list.style.scrollBehavior = previousBehavior;
          });
        }
      });
    });
  }, [refreshLoading]);
  if (loading) return <HomeSkeleton />;

  return (
  <div className="homeView">
    {refreshLoading && <div className="homeViewMiniLoader" />}
      
      {visibleSections.map((section,idSection) => {
        const isOpenSpecial = section.pageKind === "special" && (section.specialKind === "open_lunch" || section.specialKind === "open_dinner");
        const isAllRestaurantsSpecial = section.pageKind === "special" && section.specialKind === "all_restaurants";
        const canRedirect = !!section.slugPath && section.pageKind !== "special";
        const canApplyFilter = !!section.filterValue && !section.slugPath;
        const canShowAll = canRedirect || canApplyFilter || isOpenSpecial || isAllRestaurantsSpecial;

        const isCitySection = section.pageKind === 'city'; 

        const handleShowAll = () => {
          setCurrentNeighborhood([])
          setFetchResto(false);
          setShowWaitingSearch(true)
           setTimeout(() => {
              setShowWaitingSearch(false)
          },1000)
          if (isOpenSpecial) {
            const today = new Date();
            window.scrollTo({top:0,behavior:"auto"})
            setDate_selected(today);
            setHour_selected(section.specialKind === "open_lunch" ? "12h30" : "20h00");
            setFilterChoose([section.specialKind === "open_lunch" ? "Ouvert ce midi" : "Ouvert ce soir"])
            return;
          }
          if(isAllRestaurantsSpecial) {
            navigate("/restaurants", { state: { topBarMode: "search" } });
            return;
          }
          if (canRedirect && section.slugPath) {
            navigate(section.slugPath, { state: { topBarMode: "search" } });
            return;
          }
          if (canApplyFilter && section.filterValue) {
            setFilterChoose((prev) =>
              prev.includes(section.filterValue as string)
                ? prev
                : [...prev, section.filterValue as string]
            );
          }
           
        };
        return <React.Fragment key={section.id}><section className="homeSection">
          <div className="homeSectionHeader" >
            
            <div className={`contentSectionHeader ${canShowAll ? 'pointer lowOpacityHover' : ''}`} onClick={() => {if (!canShowAll){return}else{handleShowAll()}}}>
              <h2 className='midTitle'>{isCitySection && section.cityName ? `Trouvez un bon restaurant ${cityWithPreposition(section.cityName)}` : section.title}</h2>
              {canShowAll && <FiChevronRight className="iconChevron" /> }
            </div>
            {idSection === 0 && <HookSortedPart fetchSections={fetchSections} baseSortSection={baseSortSection}/>}
          </div>
          <div className="homeSectionListWrapper ">
            <div className="shadowLeftSection"></div>
            <div className="shadowRightSection"  style={{ right: `${scrollbarWidth - 1}px` }}></div>
            {scrollStates[section.id]?.canScrollLeft && (
              <div className="homeSectionScrollButton left" onClick={() => scrollSection(section.id, "left")} >
                <FiChevronLeft size={22}/>
              </div>
            )}
          <div className={`homeSectionList ${(canApplyFilter || canRedirect) ? 'canApply' : ''}`}  ref={(elem) => {listRefs.current[section.id] = elem}}  onScroll={() => {
    updateScrollState(section.id);
  }}>
                {section.restaurants.map((resto,indexResto) => {
                  const eager = isDesktop && indexResto < 5;
                  const priority = idSection === 0 && indexResto < 2;
                  return <ContainerRestauLight key={resto.idrestaurant} resto={resto} isHighlighted={!disableHighlight && mobileActiveIndexes[section.id] === indexResto} priority={priority} eager={eager}/>
                })}
                {canShowAll && section.restaurants.length >= 7 && <div className="containerRedirectBloc flexAllCenter" ><div className="redirectBloc XsmallText pointer lowOpacityHover smallWhiteRoundButton" onClick={handleShowAll}>Tout afficher <div className="flexAllCenter"><FiChevronRight className="iconFirst"/></div></div> </div>}

              </div>

              {scrollStates[section.id]?.canScrollRight && (
                <div className="homeSectionScrollButton right" onClick={() => scrollSection(section.id, "right")} >
                  <FiChevronRight size={22}/>
                </div>
              )}
            </div>
          <div className={`homeSectionMobileIndicator ${section.restaurants.length >= 10 ? 'largeSection' : ''}`}>
            {section.restaurants.map((_, indexResto) => (
               <div key={indexResto} className="indicatorHitArea" onClick={() => handleGoToBlock(section.id, indexResto)}>
                  <div
                    className={`indicatorLine ${
                      mobileActiveIndexes[section.id] === indexResto ? "isActive" : ""
                    }`}
                    onClick={() => handleGoToBlock(section.id,indexResto)}
                  />
                </div>
            ))}
          </div>
        </section>
        {idSection < visibleSections.length - 1 && <div className="separationSection"></div>}
        </React.Fragment>
      })}
      {sectionPaginationEnabled && canShowMoreSections && (
          <div className="homeSectionsPagination XsmallText">
            {canShowMoreSections && (
              <div className="midWhiteRoundButton" onClick={handleShowMoreSections}>
                Afficher plus <FiChevronDown size={18}/>
              </div>
            )}
          </div>
      )}
    </div>)
};

export default HomeView;